/**
 * PRDGenerator - AI-Powered Autonomous PRD Generation (Refactored).
 *
 * Simplified component using XState-based coordinator for session lifecycle.
 * Reduces from 1,195 lines to ~300 lines by extracting state machine logic.
 *
 * Architecture:
 * - PRDSessionCoordinator: Manages state machine and API calls
 * - PRDProgressDisplay: Renders UI based on state
 * - PRDGenerator: Thin wrapper for composition
 */

import { JSX, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@heroui/react';

import { usePRDStore } from '~/stores/prdStore';
import { useUIStore } from '~/stores/uiStore';

import { ErrorBoundary } from '~/components/ErrorBoundary';
import { PRDProgressDisplay } from '~/components/PRDSession/PRDProgressDisplay';
import { PRDSessionCoordinator } from '~/components/PRDSession/PRDSessionCoordinator';

import { PRDClassificationBadge } from './PRDClassificationBadge';

const SESSION_CLEANUP_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

export interface PRDGeneratorProps {
  existingPRD?: string;
  onSavePRD?: (prdContent: string) => void;
  projectDetails?: string; // User-provided context/requirements
  projectId: string;
  projectName: string;
}

export function PRDGenerator({
  existingPRD,
  onSavePRD: _onSavePRD,
  projectDetails,
  projectId,
  projectName,
}: PRDGeneratorProps): JSX.Element {
  const { clearPendingPRDAutoStart, pendingPRDAutoStart } = useUIStore();

  // Effect: Periodic cleanup of expired sessions
  useEffect(() => {
    const { cleanupExpiredSessions } = usePRDStore.getState();
    const interval = setInterval(() => {
      cleanupExpiredSessions();
    }, SESSION_CLEANUP_INTERVAL_MS);

    return (): void => {
      clearInterval(interval);
    };
  }, []);

  return (
    <PRDSessionCoordinator
      projectId={projectId}
      projectName={projectName}
      projectDetails={projectDetails}
      existingPRD={existingPRD}
    >
      {({
        cancelSession,
        context,
        hasError,
        isComplete,
        isGenerating,
        isLoading,
        retry,
        startOver,
        startSession,
        state,
      }) => {
        // Handle auto-start signal from "Generate PRD with AI" button
        // When pendingPRDAutoStart matches this project, auto-trigger session start
        // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: Refactor to lift this logic out of render prop
        useEffect(() => {
          if (
            pendingPRDAutoStart === projectId &&
            !context.userRequestedStart &&
            state === 'idle'
          ) {
            startSession();
            clearPendingPRDAutoStart();
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps -- startSession is stable
        }, [pendingPRDAutoStart, projectId, context.userRequestedStart, state]);

        // Show "Start PRD Generation" button when idle
        if (state === 'idle' && !context.userRequestedStart) {
          return (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="shrink-0 px-4 py-3 border-b border-border bg-surface-elevated">
                <h2 className="text-base font-semibold">Generate PRD with AI</h2>
              </div>
              <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center max-w-md">
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    Ready to Generate Your PRD
                  </h3>
                  <p className="text-sm text-muted mb-6">
                    Our AI assistant will guide you through a conversation to create a comprehensive
                    Product Requirements Document for your project.
                  </p>
                  <Button
                    color="primary"
                    size="lg"
                    onPress={() => {
                      startSession();
                    }}
                  >
                    Start PRD Generation
                  </Button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="shrink-0 px-4 py-3 border-b border-border bg-surface-elevated">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold">Generate PRD with AI</h2>
                {context.sessionId && (
                  <Button
                    variant="light"
                    color="danger"
                    size="sm"
                    onPress={() => void cancelSession()}
                    isLoading={false}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>

            {/* Classification Badge */}
            {context.sessionId && (
              <PRDClassificationBadge
                scenarioType={context.scenarioType}
                projectSize={context.projectSize}
                classificationConfidence={context.classificationConfidence}
                classificationReasoning={context.classificationReasoning}
                isLoading={isLoading && !context.scenarioType && !context.projectSize}
              />
            )}

            {/* Conflict message - shown when another user is generating PRD */}
            {context.conflictOwner && (
              <div className="shrink-0 px-4 py-3 bg-warning-50 dark:bg-warning-900/20 border-b border-warning-200 dark:border-warning-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-warning-600 dark:text-warning-400">⏳</span>
                    <p className="text-sm text-warning-800 dark:text-warning-200">
                      {context.conflictOwner.displayName} is generating a PRD for this project
                    </p>
                  </div>
                  <Button
                    variant="light"
                    size="sm"
                    onPress={() => {
                      void startOver();
                    }}
                  >
                    Refresh
                  </Button>
                </div>
              </div>
            )}

            {/* Error banner */}
            {context.error && !context.conflictOwner && (
              <div className="shrink-0 px-4 py-3 bg-danger-50 dark:bg-danger-900/20 border-b border-danger-200 dark:border-danger-800">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-danger-800 dark:text-danger-200">{context.error}</p>
                  <button
                    type="button"
                    onClick={() => {
                      void startOver();
                    }}
                    className="p-1 rounded-md hover:bg-danger-100 dark:hover:bg-danger-900/40 transition-colors"
                    aria-label="Dismiss"
                  >
                    <XMarkIcon className="w-4 h-4 text-danger-800 dark:text-danger-200" />
                  </button>
                </div>
              </div>
            )}

            {/* Main content */}
            <ErrorBoundary
              fallback={
                <div className="flex-1 flex items-center justify-center p-8">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold text-error dark:text-error mb-2">
                      Error loading PRD generation
                    </h3>
                    <p className="text-sm text-muted mb-4">
                      An error occurred while loading the interface.
                    </p>
                    <Button color="primary" onPress={() => window.location.reload()}>
                      Refresh Page
                    </Button>
                  </div>
                </div>
              }
            >
              <PRDProgressDisplay
                state={state}
                projectId={projectId}
                isGenerating={isGenerating}
                isComplete={isComplete}
                hasError={hasError}
                error={context.error}
                sessionState={context.sessionState}
                onRetry={retry}
                onStartOver={() => {
                  void startOver();
                }}
              />
            </ErrorBoundary>
          </div>
        );
      }}
    </PRDSessionCoordinator>
  );
}
